import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { createTableRows } from "../../../shared/customTables/utils/createTableRows";
import { useModal } from "../../../shared/modal/useModal.vm";
import { TableCellData } from "../../../shared/customTables/cells/cellsSwitcher/CellsSwitcher.styled";
import { CellTime } from "../../../shared/customTables/cells/cellTime/CellTime";
import { ActionButton } from "../../../shared/customTables/button/ActionButton";
import { IAction, IHeadCell } from "../../../../interfaces/common/table";
import { useAuthStore, useCasesStore } from "../../../../store/hooks";
import { useCasesApi } from "../../../../apiHooks";
import { ModalType } from "../../../../constants/modalType";
import { CaseStatus } from "../../../../interfaces/user/draftCase/draft";
import {
  AdminLink,
  generateLink,
  SplitData,
  splitData,
  UserLink,
} from "../../../../utils";
import { StatusText } from "../../../shared/admin/status/Status.styled";
import { IconContainer } from "./Cases.styled";
import theme from "../../../../theme/theme";
import { ReactComponent as PublicIcon } from "../../../../assets/icon/public.svg";
import { ReactComponent as UnpublicIcon } from "../../../../assets/icon/unpublic.svg";
import { ReactComponent as HistoryIcon } from "../../../../assets/icon/history.svg";
import { ReactComponent as ViewLensIcon } from "../../../../assets/icon/viewLens.svg";
import { ReactComponent as CheckboxCheckIcon } from "../../../../assets/icon/checkboxCheck.svg";
import { ReactComponent as CloseIcon } from "../../../../assets/icon/close.svg";
import { RoleFunction } from "../../../../constants/projectUserRoles";
import { SelectString } from "../../../shared/ui/selectString/SelectString";
import { Grid } from "@mui/material";
import { IOptionString } from "../../../../interfaces/common/form/option";
import { filterCases, getCaseIDFromKey } from "./useCases.helper";
import { IForm } from "../../../../interfaces/admin/cases/cases";

const optionsScientific = ["Do rozpatrzenia", "Zaakceptowany", "Do edycji"];

const optionsAccepting = [
  "Do rozpatrzenia",
  "Zaakceptowany",
  "Do edycji",
  "W akceptacji II poziomu",
];

type SelectedStatus =
  | "Do rozpatrzenia"
  | "Zaakceptowany"
  | "Do edycji"
  | "W akceptacji II poziomu"
  | "Status";

const headCells: IHeadCell[] = [
  { id: "title", numeric: false, label: "Nazwa opisu przypadku" },
  { id: "customId", numeric: false, label: "Id opisu" },
  { id: "doctor", numeric: false, label: "Autor" },
  { id: "form", numeric: false, label: "Formularz" },
  {
    id: "sentToAcceptAt",
    numeric: false,
    label: "Data przesłania do akceptacji",
  },
  {
    id: "userAccepting",
    numeric: true,
    label: "Akceptujący  I stopnia",
    labelElement: (
      <>
        Akceptujący <br /> I stopnia
      </>
    ),
  },
  {
    id: "userScientific",
    numeric: true,
    label: "Akceptujący II stopnia",
    labelElement: (
      <>
        Akceptujący <br /> II stopnia
      </>
    ),
  },
  {
    id: "firstSendToAcceptAt",
    numeric: false,
    label: "Data pierwszego przesłania do akceptacji",
  },
];

export const useCases = () => {
  const [actionCustomId, setCustomActionId] = useState<string | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<CaseStatus | "">("");

  const [selectedForm, setSelectedForm] = useState<number | "">("");
  const navigate = useNavigate();
  const casesStore = useCasesStore();
  const { cases, forms, isCasesLoaded } = casesStore;
  const [isPublic, setIsPublic] = useState<boolean | null>(null);
  const { getCases, acceptCase, editStatusCase, rejectCase } = useCasesApi();
  const { handleModal, isModalOpened, closeModal, modalType } = useModal();
  const authStore = useAuthStore();
  const { roleFunction } = authStore;

  const isScientific = roleFunction === RoleFunction.ROLE_SCIENTIFIC;

  useEffect(() => {
    if (!isCasesLoaded) {
      if (cases === null) {
        void getCases();
      } else {
        void getCases(false);
      }
    }
  }, [getCases, cases, isCasesLoaded]);

  useEffect(() => {
    return () => {
      casesStore.resetCases();
    };
  }, [casesStore]);

  const changeStatusHandler = (customId?: number | string) => {
    if (isScientific && customId) {
      setCustomActionId(String(customId));
      if (cases) {
        const element = cases.find((element) => element.customId === customId);
        if (element) {
          setIsPublic(element.isPublic);
        }
      }

      handleModal(ModalType.EDIT_STATUS_CASE_MODAL);
    }
  };

  const generatePublicationStatusIcon = (
    id: number | string,
    customId?: string | number
  ) => {
    if (cases) {
      const element = cases.find((element) => element.customId === customId);
      const isAccept = element?.status === CaseStatus.ACCEPT;
      if (element && element.status !== CaseStatus.SENT) {
        return (
          <IconContainer
            $isAction={isAccept}
            onClick={() => isAccept && changeStatusHandler(customId)}
          >
            {element.isPublic ? <PublicIcon /> : <UnpublicIcon />}{" "}
          </IconContainer>
        );
      }
    }
    return null;
  };

  const generateHistoryBackground = (
    id: number | string,
    customId?: string | number
  ) => {
    if (cases) {
      const element = cases.find((element) => element.customId === customId);

      if (element) {
        return element.countComments > 0 ? "white" : "transparent";
      }
    }
    return "transparent";
  };

  const generateHistoryIcon = (
    id: number | string,
    customId?: string | number
  ) => {
    if (cases) {
      const element = cases.find((element) => element.customId === customId);
      if (element) {
        return element.countComments > 0 ? <HistoryIcon /> : null;
      }
    }
    return null;
  };

  const generateHistoryDisabled = (
    id: number | string,
    customId?: string | number
  ) => {
    if (cases) {
      const element = cases.find((element) => element.customId === customId);
      if (element) {
        return !(element.countComments > 0);
      }
    }
    return false;
  };

  const navigateHistoryHandler = (
    id: number | string,
    userContract?: number,
    customId?: string | number
  ) => {
    if (!generateHistoryDisabled(+id, customId)) {
      const findCase = cases && cases.find((c) => c.customId === customId);
      if (findCase) {
        navigate(
          generateLink(AdminLink.HISTORY, [
            [":caseId", getCaseIDFromKey(id)],
            [":area", String(findCase.area.name)],
            [":form", String(findCase.form.name)],
          ])
        );
      }
    }
  };

  const viewHandler = (
    id: number | string,
    userContract?: number,
    customId?: string | number
  ) => {
    const findCase = cases && cases.find((c) => c.customId === customId);
    if (findCase) {
      navigate(
        generateLink(UserLink.VIEW_CASE, [
          [":caseId", getCaseIDFromKey(id)],
          [":area", String(findCase.area.name)],
          [":form", String(findCase.form.name)],
        ])
      );
    }
  };

  const acceptCaseModalHandler = async (
    id: number | string,
    userContract?: number,
    customId?: string | number
  ) => {
    setCustomActionId(String(customId));
    setIsPublic(null);
    handleModal(ModalType.ACCEPT_CASE_MODAL);
  };

  const acceptCaseModalAcceptingStart = async () => {
    if (actionCustomId) {
      const element =
        cases && cases.find((element) => element.customId === actionCustomId);

      if (element) {
        const res = await acceptCase({
          area: element.area.name,
          form: element.form.name,
          caseId: String(element.id),
          isPublic: isPublic ?? true,
        });
        if (res) {
          handleModal(ModalType.ACCEPT_CASE_MODAL);
        }
      }
    }
  };

  const acceptCaseByAccepting = (
    id: number | string,
    userContract?: number,
    customId?: string | number
  ) => {
    setCustomActionId(String(customId));
    setIsPublic(null);
    handleModal(ModalType.ACCEPT_CASE_MODAL_START);
  };

  const acceptCaseHandler = async (isPublic: boolean) => {
    setIsPublic(isPublic);
    if (actionCustomId !== null) {
      const element =
        cases && cases.find((element) => element.customId === actionCustomId);

      if (element) {
        const res = await acceptCase({
          area: element.area.name,
          form: element.form.name,
          caseId: String(element.id),
          isPublic: isPublic ?? true,
        });
        if (res) {
          setCustomActionId(null);
          handleModal(ModalType.ACCEPT_CASE_CONFIRMATION_MODAL);
        }
      }
    }
  };

  const editStatusCaseHandler = async (isPublic: boolean) => {
    setIsPublic(isPublic);
    if (actionCustomId !== null) {
      const element =
        cases && cases.find((element) => element.customId === actionCustomId);
      if (element) {
        const res = await editStatusCase({
          id: element.id,
          area: element.area.name,
          form: element.form.name,
          isPublic,
        });
        if (res) {
          await getCases();
          setCustomActionId(null);
          setIsPublic(null);
          closeModal();
        }
      }
    }
  };

  const rejectCaseModalHandler = (
    id: number | string,
    userContract?: number,
    customId?: string | number
  ) => {
    setCustomActionId(String(customId));
    handleModal(ModalType.REJECT_CASE_MODAL);
  };

  const rejectCaseHandler = async (comment: string) => {
    if (actionCustomId !== null) {
      const element =
        cases && cases.find((element) => element.customId === actionCustomId);
      if (element) {
        const res = await rejectCase({
          area: element.area.name,
          form: element.form.name,
          caseId: String(element.id),
          comment,
        });
        if (res) {
          setCustomActionId(null);
          handleModal(ModalType.REJECT_CASE_CONFIRMATION_MODAL);
        }
      }
    }
  };

  const generateStatusActions = (
    id: number | string,
    userContract?: number,
    customId?: string | number
  ) => {
    if (cases) {
      const element = cases.find((element) => element.customId === customId);
      if (element) {
        switch (element.status) {
          case CaseStatus.REJECT:
            return (
              <StatusText color={theme.colors.red}>
                <span>do edycji</span>
              </StatusText>
            );
          case CaseStatus.ACCEPT:
            return (
              <StatusText color={theme.colors.green}>
                <span>zaakceptowany</span>
              </StatusText>
            );
          case CaseStatus.TO_ACCEPT:
            return roleFunction === RoleFunction.ROLE_SCIENTIFIC ? (
              <>
                <ActionButton
                  key={"accept"}
                  customId={customId}
                  id={id as number}
                  actionHandler={acceptCaseModalHandler}
                  background={theme.colors.green}
                  width={52}
                  actionElement={<CheckboxCheckIcon />}
                />
                <ActionButton
                  customId={customId}
                  key={"reject"}
                  id={id as number}
                  actionHandler={rejectCaseModalHandler}
                  background={theme.colors.red}
                  width={52}
                  actionElement={<CloseIcon />}
                />
              </>
            ) : (
              <StatusText color={theme.colors.blue2}>
                <span>w akceptacji</span>
              </StatusText>
            );
          case CaseStatus.SENT:
            return (
              <>
                <ActionButton
                  key={"accept"}
                  customId={customId}
                  id={id as number}
                  actionHandler={acceptCaseByAccepting}
                  background={theme.colors.green}
                  width={52}
                  actionElement={<CheckboxCheckIcon />}
                />
                <ActionButton
                  key={"reject"}
                  id={id as number}
                  customId={customId}
                  actionHandler={rejectCaseModalHandler}
                  background={theme.colors.red}
                  width={52}
                  actionElement={<CloseIcon />}
                />
              </>
            );
        }
      }
    }
    return null;
  };

  const generateActions = () => {
    const actions: IAction[] = [
      {
        key: "send",
        background: "white",
        actionElement: generatePublicationStatusIcon,
        width: 52,
        isDisabled: true,
      },
      {
        key: "history",
        background: generateHistoryBackground,
        actionElement: generateHistoryIcon,
        actionHandler: navigateHistoryHandler,
        width: 52,
        isDisabled: generateHistoryDisabled,
      },
      {
        key: "view",
        background: theme.colors.blue2,
        actionHandler: viewHandler,
        actionElement: <ViewLensIcon />,
        width: 52,
      },
      {
        key: "status",
        custom: generateStatusActions,
        width: 104,
      },
    ];
    return actions;
  };

  const cellsSwitcher = (
    keyName: string,
    value: string | IForm,
    onClick?: () => void
  ) => {
    const specialValue: SplitData =
      keyName === "sentToAcceptAt" || keyName === "firstSendToAcceptAt"
        ? splitData(value.toString())
        : ["-", ""];

    switch (keyName) {
      case "id":
        return null;
      case "sentToAcceptAt":
        return (
          <TableCellData>
            <CellTime specialValue={specialValue} />
          </TableCellData>
        );
      case "firstSendToAcceptAt":
        return (
          <TableCellData>
            <CellTime specialValue={specialValue} />
          </TableCellData>
        );
      case "form":
        return <TableCellData>{(value as IForm).title}</TableCellData>;
      default:
        return <TableCellData>{value.toString()}</TableCellData>;
    }
  };

  const changeStatus = (selectedStatus: SelectedStatus) => {
    let status: CaseStatus | "" = "";

    if (selectedStatus === "Do rozpatrzenia") {
      status =
        roleFunction === RoleFunction.ROLE_ACCEPTING
          ? CaseStatus.SENT
          : CaseStatus.TO_ACCEPT;
    } else if (selectedStatus === "Zaakceptowany") {
      status = CaseStatus.ACCEPT;
    } else if (selectedStatus === "Do edycji") {
      status = CaseStatus.REJECT;
    } else if (selectedStatus === "W akceptacji II poziomu") {
      status = CaseStatus.TO_ACCEPT;
    }
    setSelectedStatus(status);
  };

  const convertValue = (caseStatus: CaseStatus | ""): SelectedStatus => {
    if (
      caseStatus === CaseStatus.SENT ||
      (caseStatus === CaseStatus.TO_ACCEPT &&
        roleFunction === RoleFunction.ROLE_SCIENTIFIC)
    )
      return "Do rozpatrzenia";
    else if (caseStatus === CaseStatus.ACCEPT) return "Zaakceptowany";
    else if (
      caseStatus === CaseStatus.TO_ACCEPT &&
      roleFunction === RoleFunction.ROLE_ACCEPTING
    )
      return "W akceptacji II poziomu";
    else if (caseStatus === CaseStatus.REJECT) return "Do edycji";
    return "Status";
  };

  const selectedFormHandler = (value: number) => {
    setSelectedForm(value);
    forms?.find((form) => form.id === value);
  };

  const addedContent = (
    <Grid container gap={2} flexWrap={"nowrap"}>
      <Grid item xs={6} maxWidth={"300px !important"}>
        <SelectString
          colorLabel={theme.colors.grey1}
          borderColor={theme.colors.grey5}
          background={"white"}
          defaultValue={"Status"}
          elementName={"status"}
          selectValue={convertValue(selectedStatus)}
          handleChange={(e) => changeStatus(e.target.value as SelectedStatus)}
          optionsArray={
            roleFunction === RoleFunction.ROLE_ACCEPTING
              ? optionsAccepting
              : optionsScientific
          }
        />
      </Grid>
      <Grid item xs={6} mr={2} maxWidth={"300px !important"}>
        <SelectString
          colorLabel={theme.colors.grey1}
          borderColor={theme.colors.grey5}
          background={"white"}
          defaultValue={"Formularz"}
          elementName={"area"}
          selectValue={selectedForm}
          handleChange={(e) => {
            selectedFormHandler(+e.target.value);
          }}
          optionsArray={
            forms
              ? forms.map((form) => {
                  const option = {
                    id: String(form.id),
                    title: form.title,
                  } as IOptionString;
                  return option;
                })
              : []
          }
        />
      </Grid>
    </Grid>
  );

  const filteredData = filterCases(cases, selectedStatus, selectedForm);
  const tableDataRows = createTableRows(filteredData ?? [], headCells, false);

  return {
    casesIsNotNull: cases !== null,
    cellsSwitcher,
    generateActions,
    acceptCaseHandler,
    editStatusCaseHandler,
    acceptCaseModalAcceptingStart,
    rejectCaseHandler,
    addedContent,
    headCells,
    tableDataRows,
    modalType,
    isModalOpened,
    closeModal,
    isPublic,
    roleFunction,
  };
};
